import { useState } from 'react';
import { erApiFeil } from '../api/feil.ts';
import { HENDELSER, senderVed, useLeveringer, useOppdaterWebhook, useOpprettWebhook, useRoterSecret, useSendPaaNytt, useSlettWebhook, useTestWebhook, useVerifiserWebhook, useWebhooks, type Webhook, type WebhookEventType, type WebhookProbeResult } from '../api/useWebhooks.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Bryter } from '../kjentefeil/Bryter.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { SloeyfenUt } from './Bruk.tsx';
import { Kode, Rad, Seksjon, useRad } from './Rad.tsx';
import { Om } from '../skall/Om.tsx';
import { useMe } from '../api/useMe.ts';
import { EndreHendelser, Hendelsesgrupper } from './Hendelsesvalg.tsx';

export interface WebhooksProps {
  erEier: boolean;
  kanTeste: boolean;
  onMelding: (tekst: string) => void;
}

const tid = (iso: string) => new Date(iso).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Hendelsen i klartekst. Ledningsnavnet står ved avkryssingen og i loggen — her er det listen som skal leses. */
const hendelse = (navn: string) => HENDELSER.find((h) => h.navn === navn)?.gjelder ?? navn;

/**
 * Mottakerens status leses av to felter: uten verifisering = «Uverifisert», verifisert og av
 * = «Av», ellers «Aktiv». Ikon OG ord, aldri farge alene (DD-08).
 */
function status(w: Webhook): { ord: string; ikon: string; klasse: string } {
  if (!w.verifiedAt) return { ord: 'Uverifisert', ikon: 'warning-outline', klasse: 'panel-innst-uverifisert' };
  if (!w.active) return { ord: 'Av', ikon: 'close-circle-outline', klasse: 'panel-innst-av' };
  return { ord: 'Aktiv', ikon: 'checkmark-circle-outline', klasse: 'panel-innst-ok' };
}

/**
 * Varsler til leietakerens system (5d). Plattformen sender aldri push eller e-post selv;
 * den kaller mottakerens URL med signert JSON. Signeringshemmeligheten vises ÉN gang, ved
 * opprettelse og rotasjon (DD-20); løftet om at det ikke finnes noen «vis»-knapp står i
 * «Produkter og nøkler» og sies ikke om igjen her (B7). Ny URL må verifiseres før mottakeren
 * kan aktiveres; testknappen krever verifisert. Leveringsloggen viser de oppgitte med
 * «Send på nytt» (låst). Sletting er endelig og rammer loggen — derfor bekreftelsesdialog.
 *
 * Ordet «webhook» står i seksjonens tittel og i knappenes ledningsnavn, aldri i brødteksten
 * (DD-60): der heter det «mottaker» og «varsel».
 */
export function Webhooks({ erEier, kanTeste, onMelding }: WebhooksProps) {
  const liste = useWebhooks();
  const opprett = useOpprettWebhook();
  // H3.5: gruppen Hypoteser finnes bare når leietakeren har rommet (422 room_not_enabled ellers).
  const medHypoteser = (useMe().data?.rooms ?? []).includes('hypoteser');
  const { tekniske } = useRad();
  const [nyUrl, setNyUrl] = useState('');
  const [nyeHendelser, setNyeHendelser] = useState<WebhookEventType[]>(['submission.status_changed', 'submission.replied']);
  const [secret, setSecret] = useState<{ url: string; secret: string; grunn: 'opprettet' | 'rotert' } | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  const eierGrunn = erEier ? null : 'Kun eier kan endre mottakere';
  const urlGyldig = /^https:\/\/\S+$/.test(nyUrl.trim());
  const opprettGrunn = eierGrunn ?? (!nyUrl.trim() ? 'URL mangler' : !urlGyldig ? 'URL-en må være https' : nyeHendelser.length === 0 ? 'Velg minst én hendelse' : opprett.isPending ? 'Registrerer …' : null);

  const visFeil = (e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');

  const registrer = () => {
    if (opprettGrunn) return;
    opprett
      .mutateAsync({ url: nyUrl.trim(), events: nyeHendelser })
      .then((w) => {
        setSecret({ url: w.url, secret: w.secret ?? '', grunn: 'opprettet' });
        setNyUrl('');
        onMelding(`Mottakeren ${w.url} er registrert. Verifiser den før den kan aktiveres.`);
      })
      .catch(visFeil);
  };

  const mottakere = liste.data ?? [];
  const aktive = mottakere.filter((w) => w.verifiedAt && w.active).length;
  const uverifiserte = mottakere.filter((w) => !w.verifiedAt).length;

  return (
    <Seksjon id="utgaaende" tittel="Utgående">
      <Rad
        id="mottakere"
        navn="Varsler til deres system"
        kode="webhooks"
        handling="Endre"
        verdi={
          liste.isPending ? (
            'Henter …'
          ) : liste.isError ? (
            'Kunne ikke hentes'
          ) : mottakere.length === 0 ? (
            'Ingen mottakere'
          ) : (
            <>
              <strong>
                {mottakere.length} {mottakere.length === 1 ? 'mottaker' : 'mottakere'}
              </strong>{' '}
              · {aktive} aktive{uverifiserte > 0 ? ` · ${uverifiserte} uverifisert` : ''}
            </>
          )
        }
        verdiSmal={liste.isPending ? 'Henter …' : `${mottakere.length} ${mottakere.length === 1 ? 'mottaker' : 'mottakere'}`}
      >
        <p className="panel-tekst-liten">Innspel varsler systemet deres, og dere varsler videre i egen kanal.</p>
        <SloeyfenUt />

        {liste.isPending ? (
        <p role="status" className="panel-tekst-liten">
          Laster mottakere …
        </p>
      ) : liste.isError ? (
        <p role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> Kunne ikke laste mottakerne. {erApiFeil(liste.error) ? liste.error.brukertekst : ''}
        </p>
      ) : liste.data && liste.data.length === 0 ? (
        <p className="panel-tekst-liten" data-testid="ingen-webhooks">
          Ingen mottaker registrert. Uten en får dere ingen varsler — registrer én under.
        </p>
      ) : null}

      <ul className="panel-webhooks" aria-label="Mottakere">
        {(liste.data ?? []).map((w) => (
          <WebhookKort key={w.id} w={w} erEier={erEier} kanTeste={kanTeste} medHypoteser={medHypoteser} onSecret={(s) => setSecret({ url: w.url, secret: s, grunn: 'rotert' })} onMelding={onMelding} onFeil={visFeil} />
        ))}
      </ul>

      <form
        className="panel-innst-ny-webhook"
        aria-label="Ny mottaker"
        onSubmit={(e) => {
          e.preventDefault();
          registrer();
        }}
      >
        <label className="panel-felt">
          <span>Ny mottaker — URL (https)</span>
          <input type="url" value={nyUrl} onChange={(e) => setNyUrl(e.target.value)} placeholder="https://api.leietaker.example/innspel/webhook" disabled={!erEier} data-testid="webhook-url" />
          <span className="panel-etikett">Opprettes inaktiv og uverifisert.</span>
        </label>
        <fieldset className="panel-innst-hendelser">
          <legend className="panel-tekst-liten">Hendelser</legend>
          <Hendelsesgrupper valgte={nyeHendelser} kanEndre={erEier} medHypoteser={medHypoteser} onEndre={setNyeHendelser} />
        </fieldset>
        <Knapp label="Registrer mottaker" primaer onClick={registrer} grunn={opprettGrunn} testid="webhook-opprett" />
      </form>

      {feil ? (
        <p role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> {feil}
          <button type="button" className="panel-knapp-liten" onClick={() => setFeil(null)}>
            Lukk
          </button>
        </p>
      ) : null}

        <Om tittel="varslene" testid="om-varslene">
          <p>Plattformen sender aldri push eller e-post selv. Den kaller URL-en under med signert JSON, dere kjenner igjen pseudonymet og sender videre i deres egen kanal. Nyttelasten bærer aldri innspillsteksten.</p>
          <p>Signaturen og hendelses-id-en står i hodene <code>X-Innspel-Signature</code> og <code>X-Innspel-Event-Id</code>. Vi venter i 10 sekunder, følger ingen videresending, og avviser adresser i private nett.</p>
          <p>En levering prøves fem ganger — først straks, så etter 1 minutt, 5 minutter, 30 minutter og 2 timer. Gir mottakeren fortsatt ikke et gyldig svar, legges leveringen død, og dere kan sende den på nytt fra leveringsloggen.</p>
          {medHypoteser ? <p>Hypotesehendelsene sier hvem som skal varsles som panelbrukerens id og rolle — aldri navn eller e-post. Id-en står under navnet i Panelbrukere når «Vis tekniske navn» er på. Innsendere uten navn står aldri i lista.</p> : null}
          <p>Signeringshemmeligheten lagres ikke. Den utledes fra en masternøkkel i driftsmiljøet og vises én gang, når mottakeren registreres. Rotasjon lager en ny som vises én gang; den gamle slutter å gjelde straks.</p>
        </Om>

        {secret ? <HemmelighetDialog url={secret.url} secret={secret.secret} grunn={secret.grunn} onLukk={() => setSecret(null)} /> : null}
      </Rad>

      {/* Tabellen står ALLTID, også med raden lukket: den er oversikten, og den har ikke en
          egen åpning. «Siste levering» finnes ikke her — det er ett kall per mottaker. */}
      {mottakere.length > 0 ? (
        <div className="panel-innst-tabell">
          <table className="panel-tabell" aria-label="Mottakere og status">
            <thead>
              <tr>
                <th scope="col">Mottaker</th>
                <th scope="col">Sender ved</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {mottakere.map((w) => {
                const st = status(w);
                return (
                  <tr key={w.id} data-testid={`mottaker-${w.id}`}>
                    <td className="panel-innst-url">{w.url}</td>
                    <td>
                      {senderVed(w.events)}
                      {tekniske ? <Kode>{w.events.join(' · ')}</Kode> : null}
                    </td>
                    <td>
                      <span className={`panel-innst-status ${st.klasse}`}>
                        <Ikon navn={st.ikon} /> {st.ord}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Seksjon>
  );
}

function WebhookKort({ w, erEier, kanTeste, medHypoteser, onSecret, onMelding, onFeil }: { w: Webhook; erEier: boolean; kanTeste: boolean; medHypoteser: boolean; onSecret: (secret: string) => void; onMelding: (t: string) => void; onFeil: (e: unknown) => void }) {
  const oppdater = useOppdaterWebhook();
  const slett = useSlettWebhook();
  const verifiser = useVerifiserWebhook();
  const test = useTestWebhook();
  const roter = useRoterSecret();
  const [visLogg, setVisLogg] = useState(false);
  const [endreHendelser, setEndreHendelser] = useState(false);
  const [bekreftSlett, setBekreftSlett] = useState(false);
  const [probe, setProbe] = useState<{ hva: 'verifisering' | 'test'; res: WebhookProbeResult } | null>(null);
  const { tekniske } = useRad();
  const eierGrunn = erEier ? null : 'Kun eier kan endre mottakere';
  const verifisert = Boolean(w.verifiedAt);

  return (
    <li className="panel-webhook" data-testid={`webhook-${w.id}`}>
      <div className="panel-webhook-hode">
        <code className="panel-webhook-url">{w.url}</code>
        <span className={`panel-brikke${verifisert ? '' : ' panel-brikke-varsel'}`} data-testid={`webhook-verifisert-${w.id}`}>
          <Ikon navn={verifisert ? 'shield-checkmark-outline' : 'warning-outline'} /> {verifisert ? `Verifisert ${tid(w.verifiedAt!)}` : 'Ikke verifisert — ingen levering'}
        </span>
        <Bryter
          label="Aktiv"
          ikon="ellipse-outline"
          paa={w.active}
          grunn={eierGrunn ?? (!verifisert && !w.active ? 'Verifiser først — en aktiv mottaker på en uverifisert adresse er det verifiseringen finnes for å hindre' : null)}
          onChange={(v) => oppdater.mutateAsync({ id: w.id, body: { active: v } }).then(() => onMelding(`Mottakeren er ${v ? 'aktiv' : 'satt på pause'}.`)).catch(onFeil)}
          testid={`webhook-aktiv-${w.id}`}
        />
      </div>
      <p className="panel-tekst-liten">
        Varsler om: {senderVed(w.events)}
        {tekniske ? <Kode>{w.events.join(' · ')}</Kode> : null}
      </p>
      <div className="panel-handlinger">
        <Knapp label="Verifiser" onClick={() => verifiser.mutateAsync(w.id).then((r) => { setProbe({ hva: 'verifisering', res: r }); if (r.ok) onMelding('Mottakeren er verifisert og kan aktiveres.'); }).catch(onFeil)} grunn={eierGrunn ?? (verifiser.isPending ? 'Verifiserer …' : null)} testid={`webhook-verifiser-${w.id}`} />
        <Knapp label="Send testhendelse" onClick={() => test.mutateAsync(w.id).then((r) => setProbe({ hva: 'test', res: r })).catch(onFeil)} grunn={!kanTeste ? 'Rollen leser kan ikke sende testhendelser' : !verifisert ? 'Krever verifisert mottaker' : test.isPending ? 'Sender …' : null} testid={`webhook-test-${w.id}`} />
        <Knapp label="Roter hemmelighet" onClick={() => roter.mutateAsync(w.id).then((r) => onSecret(r.secret ?? '')).catch(onFeil)} grunn={eierGrunn} testid={`webhook-roter-${w.id}`} />
        <Knapp label={visLogg ? 'Skjul leveringslogg' : 'Leveringslogg'} onClick={() => setVisLogg((v) => !v)} testid={`webhook-logg-${w.id}`} />
        {/* H3.5 (avvik 14): hendelsene på en mottaker som finnes — før ble de bare valgt ved registreringen. */}
        <Knapp label="Endre hendelser" onClick={() => setEndreHendelser((v) => !v)} grunn={eierGrunn} testid={`webhook-hendelser-${w.id}`} />
        <Knapp label="Slett" fare onClick={() => setBekreftSlett(true)} grunn={eierGrunn} testid={`webhook-slett-${w.id}`} />
      </div>
      {endreHendelser ? (
        <EndreHendelser
          w={w}
          medHypoteser={medHypoteser}
          onLukk={() => setEndreHendelser(false)}
          onLagret={(tekst) => {
            setEndreHendelser(false);
            onMelding(tekst);
          }}
        />
      ) : null}
      {probe ? (
        <p role="status" className={`panel-tekst-liten${probe.res.ok ? '' : ' panel-innst-feil'}`} data-testid={`webhook-probe-${w.id}`}>
          <Ikon navn={probe.res.ok ? 'checkmark-circle-outline' : 'close-circle-outline'} /> {probe.hva === 'test' ? 'Testhendelse' : 'Verifisering'}: {probe.res.ok ? 'mottakeren svarte riktig' : 'feilet'}
          {probe.res.statusCode ? ` (HTTP ${probe.res.statusCode})` : ''}
          {probe.res.detail ? ` — ${probe.res.detail}` : ''} · {tid(probe.res.at)}
        </p>
      ) : null}
      {visLogg ? <Leveringslogg webhookId={w.id} kanSende={kanTeste} onMelding={onMelding} onFeil={onFeil} /> : null}
      {bekreftSlett ? (
        <Dialog tittelId={`slett-h-${w.id}`} onLukk={() => setBekreftSlett(false)}>
          <h2 id={`slett-h-${w.id}`} className="panel-h2">
            Slette mottakeren?
          </h2>
          <p className="panel-tekst">
            <code>{w.url}</code> og hele leveringsloggen slettes. Det kan ikke angres, og dere mister varslene til en ny er registrert og verifisert.
          </p>
          <div className="panel-handlinger">
            <Knapp label="Slett mottakeren" fare onClick={() => slett.mutateAsync(w.id).then(() => onMelding('Mottakeren og leveringsloggen er slettet.')).catch(onFeil)} grunn={slett.isPending ? 'Sletter …' : null} testid={`webhook-slett-bekreft-${w.id}`} />
            <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setBekreftSlett(false)} />
          </div>
        </Dialog>
      ) : null}
    </li>
  );
}

function Leveringslogg({ webhookId, kanSende, onMelding, onFeil }: { webhookId: string; kanSende: boolean; onMelding: (t: string) => void; onFeil: (e: unknown) => void }) {
  const logg = useLeveringer(webhookId);
  const sendPaaNytt = useSendPaaNytt();
  const { tekniske } = useRad();
  if (logg.isPending) return <p role="status" className="panel-tekst-liten">Laster leveringslogg …</p>;
  if (logg.isError) return <p role="alert" className="panel-tekst-liten panel-innst-feil">Kunne ikke laste leveringsloggen.</p>;
  const rader = logg.data ?? [];
  return (
    <div className="panel-innst-logg" data-testid={`leveringslogg-${webhookId}`}>
      <p className="panel-tekst-liten">Leveringslogg · nyttelasten vises aldri her.</p>
      {rader.length === 0 ? (
        <p className="panel-tekst-liten">Ingen leveringer ennå.</p>
      ) : (
        <table className="panel-tabell">
          <thead>
            <tr>
              <th scope="col">Tid</th>
              <th scope="col">Hendelse</th>
              <th scope="col">Forsøk</th>
              <th scope="col">Svar</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rader.map((d) => (
              <tr key={d.id} className={d.status === 'dead' ? 'panel-rad-dead' : undefined} data-testid={`levering-${d.id}`}>
                <td>{tid(d.at)}</td>
                <td>
                  {hendelse(d.eventType)}
                  {tekniske ? <Kode>{d.eventType}</Kode> : null}
                </td>
                <td>{d.attempt} av 5</td>
                <td>{d.statusCode ?? 'timeout / nettverk'}</td>
                <td>
                  <span className="panel-brikke">
                    <Ikon navn={d.status === 'delivered' ? 'checkmark-circle-outline' : d.status === 'dead' ? 'close-circle-outline' : 'time-outline'} />{' '}
                    {d.status === 'delivered' ? 'Levert' : d.status === 'dead' ? 'Gitt opp etter fem forsøk' : d.status === 'failed' ? 'Feilet — prøves igjen' : `Venter${d.nextAt ? ` — neste ${tid(d.nextAt)}` : ''}`}
                  </span>
                  {d.status === 'dead' ? (
                    <Knapp label="Send på nytt" onClick={() => sendPaaNytt.mutateAsync({ id: webhookId, deliveryId: d.id }).then(() => onMelding('Leveringen prøves én gang til, med samme hendelses-id.')).catch(onFeil)} grunn={!kanSende ? 'Rollen leser kan ikke sende på nytt' : null} testid={`send-paa-nytt-${d.id}`} />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Den ene gangen signeringshemmeligheten finnes (DD-20). Lukkes den, er den borte — det står i dialogen. */
function HemmelighetDialog({ url, secret, grunn, onLukk }: { url: string; secret: string; grunn: 'opprettet' | 'rotert'; onLukk: () => void }) {
  return (
    <Dialog tittelId="hemmelighet-h" onLukk={onLukk} testid="hemmelighet-dialog">
        <h2 id="hemmelighet-h" className="panel-h2">
          <Ikon navn="lock-closed-outline" /> Signeringshemmeligheten — vist én gang
        </h2>
        <p className="panel-tekst">
          {grunn === 'opprettet' ? 'Mottakeren er registrert på' : 'Ny hemmelighet for'} <code>{url}</code>. Legg den i mottakerens konfig nå. Den finnes ikke i noe svar etterpå.
          {grunn === 'rotert' ? ' Den gamle slutter å gjelde straks.' : ''}
        </p>
        <output className="panel-hemmelighet" data-testid="hemmelighet-verdi">
          {secret}
        </output>
        <div className="panel-handlinger">
          <Knapp label="Jeg har lagret den — lukk" kbd="Esc" primaer onClick={onLukk} testid="hemmelighet-lukk" />
        </div>
    </Dialog>
  );
}
